'use client';

import { faCheck, faMinus, faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { cva, type VariantProps } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import { Button, Icon } from '@/components/ui';
import { cn } from '@/utils';

const quantityInputRoot = cva('flex items-center', {
  variants: {
    size: {
      xs: 'gap-0.5',
      sm: 'gap-1',
      md: 'gap-1.5',
      lg: 'gap-2',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const sideButton = cva('box-border min-w-0 shrink-0 rounded-full p-0', {
  variants: {
    size: {
      xs: 'h-5 w-5 min-h-5 min-w-5',
      sm: 'h-6 w-6 min-h-6 min-w-6',
      md: 'h-8 w-8 min-h-8 min-w-8',
      lg: 'h-10 w-10 min-h-10 min-w-10',
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

const quantityField = cva(
  'rounded-full border-0 bg-default-100 text-center font-medium text-foreground outline-none transition-shadow focus:ring-2 focus:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      size: {
        xs: 'h-5 w-8 px-0.5 text-xs',
        sm: 'h-6 w-10 px-1 text-sm',
        md: 'h-8 w-12 px-1 text-base',
        lg: 'h-10 w-14 px-1.5 text-lg',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

const iconSizeByVariant = {
  xs: 'text-[10px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
} as const;

export type QuantityInputProps = VariantProps<typeof quantityInputRoot> & {
  quantity: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  decreaseDisabled?: boolean;
  isLoading?: boolean;
  className?: string;
  /**
   * Called when the user clicks +/- or commits a typed value via ✓ / Enter.
   *
   * Use this for places that don't differentiate between stepper and typed
   * commits (product detail, recipe shopping cart, etc.). The cart can still
   * pass `onDecrement` / `onIncrement` / `onQuantityCommit` to wire each path
   * to a separate side-effect (e.g. immediate API for +/- vs. confirmed
   * commit on ✓).
   */
  onChange?: (value: number) => void;
  onDecrement?: () => void;
  onIncrement?: () => void;
  onQuantityCommit?: (value: number) => void;
};

const QuantityInput = ({
  quantity,
  min: minProp = 1,
  max: maxProp = 9999,
  disabled = false,
  decreaseDisabled = false,
  isLoading = false,
  size,
  className,
  onChange,
  onDecrement,
  onIncrement,
  onQuantityCommit,
}: QuantityInputProps) => {
  const [inputValue, setInputValue] = useState(String(quantity));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedSize = size ?? 'sm';
  const iconClass = iconSizeByVariant[resolvedSize];
  const buttonClass = sideButton({ size: resolvedSize });

  useEffect(() => {
    if (!isFocused) {
      setInputValue(String(quantity));
    }
  }, [quantity, isFocused]);

  const clamp = (value: number) => Math.min(maxProp, Math.max(minProp, value));

  const handleDecrement = () => {
    if (onDecrement) {
      onDecrement();
      return;
    }
    onChange?.(clamp(quantity - 1));
  };

  const handleIncrement = () => {
    if (onIncrement) {
      onIncrement();
      return;
    }
    onChange?.(clamp(quantity + 1));
  };

  const isDecreaseDisabled = disabled || isLoading || decreaseDisabled || quantity <= minProp;
  // While the input has focus, we always swap +/- for a cancel/confirm pair so
  // the user explicitly opts into the typed value (see `handleConfirmEdit`).
  // Blur without confirming reverts to `quantity` via the `useEffect` above,
  // which keeps the parent state untouched until ✓ / Enter.
  const showCommitControls = isFocused;
  const parsedInput = parseInt(inputValue, 10);
  const isConfirmDisabled =
    disabled ||
    isLoading ||
    Number.isNaN(parsedInput) ||
    parsedInput === 0 ||
    parsedInput < minProp;

  const handleCancelEdit = () => {
    setInputValue(String(quantity));
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const handleConfirmEdit = () => {
    const parsed = parseInt(inputValue, 10);
    if (Number.isNaN(parsed)) {
      setInputValue(String(quantity));
      setIsFocused(false);
      return;
    }

    const next = clamp(parsed);
    setInputValue(String(next));
    setIsFocused(false);

    if (next !== quantity) {
      const handler = onQuantityCommit ?? onChange;
      handler?.(next);
    }

    inputRef.current?.blur();
  };

  return (
    <div className={cn(quantityInputRoot({ size: resolvedSize }), className)}>
      {showCommitControls ? (
        <Button
          isIconOnly
          size="sm"
          color="danger"
          variant="bordered"
          aria-label="Cancel"
          className={buttonClass}
          isDisabled={disabled || isLoading}
          onMouseDown={e => e.preventDefault()}
          onPress={handleCancelEdit}
        >
          <Icon icon={faTimes} className={iconClass} />
        </Button>
      ) : (
        <Button
          isIconOnly
          size="sm"
          color="danger"
          variant="solid"
          aria-label="Decrease quantity"
          className={buttonClass}
          isDisabled={isDecreaseDisabled}
          onPress={handleDecrement}
        >
          <Icon icon={faMinus} className={iconClass} />
        </Button>
      )}

      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label="Quantity"
        disabled={disabled || isLoading}
        value={inputValue}
        className={quantityField({ size: resolvedSize })}
        onFocus={() => {
          setIsFocused(true);
          setInputValue(String(quantity));
        }}
        onBlur={() => {
          // Intentionally do NOT commit the typed value on blur — the user
          // must click ✓ (or press Enter) to apply. The `useEffect` above
          // resets `inputValue` back to `quantity` once `isFocused` flips
          // to false, so an abandoned edit never leaks into parent state.
          setIsFocused(false);
        }}
        onChange={e => {
          if (/^\d{0,4}$/.test(e.target.value)) {
            setInputValue(e.target.value);
          }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (showCommitControls) {
              handleConfirmEdit();
            } else {
              inputRef.current?.blur();
            }
          }
        }}
      />

      {showCommitControls ? (
        <Button
          isIconOnly
          size="sm"
          color="primary"
          variant="flat"
          aria-label="Confirm"
          className={buttonClass}
          isDisabled={isConfirmDisabled}
          isLoading={isLoading}
          onMouseDown={e => e.preventDefault()}
          onPress={handleConfirmEdit}
        >
          <Icon icon={faCheck} className={iconClass} />
        </Button>
      ) : (
        <Button
          isIconOnly
          size="sm"
          color="primary"
          variant="solid"
          aria-label="Increase quantity"
          className={buttonClass}
          isDisabled={disabled || isLoading || quantity >= maxProp}
          isLoading={isLoading}
          onPress={handleIncrement}
        >
          <Icon icon={faPlus} className={iconClass} />
        </Button>
      )}
    </div>
  );
};

export default QuantityInput;
